import { Assets } from "pixi.js";
import { PromiseCompletionSource } from "../shared/async/PromiseCompletionSource";

export class DynamicAssets {
    private readonly autoLoad: boolean;
    private readonly resourceQueue: Array<string>;
    private readonly resources: Record<string, any>;

    private loadPCS: PromiseCompletionSource<DynamicAssets>;
    private loadingResource: string;
    private destroyed: boolean;

    public get loaded(): boolean {
        return this.loadPCS == null && this.resourceQueue.length === 0;
    }

    public constructor(autoLoad: boolean = false) {
        this.autoLoad = autoLoad;
        this.resourceQueue = [];
        this.resources = {};
        this.destroyed = false;
    }

    public has(path: string): boolean {
        return this.resources[path] != null || this.loadingResource === path || this.resourceQueue.includes(path);
    }

    public isLoaded(path: string): boolean {
        return this.resources[path] != null;
    }

    public add(path: string): void {
        if (this.destroyed) {
            throw new Error("Unable to add resource, assets is destroyed");
        }
        if (this.loadingResource === path || this.resourceQueue.includes(path) || this.resources[path] != null) {
            console.warn("Resource already in assets");
            return;
        }
        this.resourceQueue.push(path);
        if (this.autoLoad) {
            this.load();
        }
    }

    public remove(path: string): void {
        if (this.destroyed) {
            return;
        }
        const indexInQueue = this.resourceQueue.indexOf(path);
        if (indexInQueue !== -1) {
            this.resourceQueue.splice(indexInQueue, 1);
            return;
        }
        if (this.loadingResource === path) {
            this.loadPCS.result.then(() => {
                delete this.resources[path];
                Assets.unload(path);
            });
            return;
        }
        if (this.resources[path] != null) {
            delete this.resources[path];
            Assets.unload(path);
        }
    }

    public removeAll(): void {
        if (this.destroyed) {
            return;
        }
        this.resourceQueue.length = 0;
        const resourcesKeys = Object.keys(this.resources);
        Assets.unload(resourcesKeys);
        for (const key of resourcesKeys) {
            delete this.resources[key];
        }
        if (this.loadPCS != null) {
            const resourceToDelete = this.loadingResource;
            this.loadPCS.result.then(() => {
                Assets.unload(resourceToDelete);
                delete this.resources[resourceToDelete];
            });
        }
    }

    public async load(): Promise<DynamicAssets> {
        if (this.loadPCS != null) {
            return await this.loadPCS.result;
        }
        if (this.destroyed) {
            throw new Error("Attemp of load destroyed resources");
        }
        if (this.resourceQueue.length === 0) {
            return this;
        }
        try {
            this.loadPCS = new PromiseCompletionSource<DynamicAssets>();
            while (!this.destroyed && this.resourceQueue.length !== 0) {
                this.loadingResource = this.resourceQueue.shift();
                this.resources[this.loadingResource] = await Assets.load(this.loadingResource);
                this.loadingResource = null;
            }
            return this;
        }
        catch (e: unknown) {
            console.error("Unable to load assets: ", e);
        }
        finally {
            this.loadPCS?.complete(this);
            this.loadPCS = null;
        }
    }

    public resource<T>(name: string): T {
        if (this.resourceQueue.includes(name) || this.loadingResource === name) {
            throw new Error(`Resource ${name} in loading queue, load before usage`);
        }
        if (this.resources[name] == null) {
            throw new Error(`Resource ${name} is not defined`);
        }
        return this.resources[name];
    }

    public destroy(): void {
        this.destroyed = true;
        if (this.loadPCS != null) {
            this.loadPCS.result.then(() => {
                Assets.unload(Object.keys(this.resources));
            });
        }
        this.resourceQueue.length = 0;
    }
}